/**
 * Absences — vue Admin / Manager
 * 4 onglets : Validations / Toutes les demandes / Soldes & types / Règles
 */
import { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  CheckCircle2, XCircle, AlertTriangle, Settings, Layers, Users,
  Check, X, RefreshCcw, Plus, MinusCircle, PlusCircle, Calendar,
  ShieldAlert, AlarmClock, Download as DownloadIcon, Trash2 as TrashIcon,
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import AbsencesOnboardingCard from '../components/absences/AbsencesOnboardingCard';

const STATUS_LABEL = {
  manager_pending: 'Manager', hr_pending: 'RH', approved: 'Approuvée',
  rejected: 'Rejetée', cancelled: 'Annulée',
};
const STATUS_BADGE = {
  manager_pending: 'bg-amber-100 text-amber-700',
  hr_pending: 'bg-blue-100 text-blue-700',
  approved: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-700',
  cancelled: 'bg-slate-100 text-slate-700',
};

// ─── Decision Dialog ───
function DecisionDialog({ open, onOpenChange, request, role, onDone }) {
  const [comment, setComment] = useState('');
  const [conflicts, setConflicts] = useState([]);

  useEffect(() => {
    if (!open || !request) return;
    setComment('');
    api.get(`/leave/requests/${request.id}/conflicts`).then(r => setConflicts(r.data?.conflicts || [])).catch(() => {});
  }, [open, request]);

  const decide = async (decision) => {
    try {
      let endpoint;
      if (role === 'director') endpoint = `/leave/requests/${request.id}/director-decision`;
      else if (role === 'hr') endpoint = `/leave/requests/${request.id}/hr-decision`;
      else endpoint = `/leave/requests/${request.id}/manager-decision`;
      await api.post(endpoint, { decision, comment });
      toast.success(decision === 'approve' ? 'Demande approuvée' : 'Demande rejetée');
      onDone();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  if (!request) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="decision-dialog">
        <DialogHeader>
          <DialogTitle>Décision — {request.employee?.firstName} {request.employee?.lastName}</DialogTitle>
          <DialogDescription>
            {request.leaveType?.name} · {request.startDate} → {request.endDate} ({request.totalDays} j)
          </DialogDescription>
        </DialogHeader>
        {conflicts.length > 0 && (
          <div className="space-y-1.5">
            {conflicts.map((c, i) => (
              <div key={i} className={`p-2 rounded-lg text-xs flex items-start gap-2 ${c.severity === 'blocking' ? 'bg-red-50 text-red-800' : 'bg-amber-50 text-amber-800'}`}>
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{c.message}</span>
              </div>
            ))}
          </div>
        )}
        {request.reason && <p className="text-xs text-slate-600 italic">"{request.reason}"</p>}
        <Textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)}
          placeholder="Commentaire (visible par l'employé)" data-testid="decision-comment" />
        <DialogFooter>
          <Button variant="outline" onClick={() => decide('reject')} className="text-red-600 hover:bg-red-50"
            data-testid="reject-btn">
            <X className="w-4 h-4 mr-2" />Rejeter
          </Button>
          <Button onClick={() => decide('approve')} className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="approve-btn">
            <Check className="w-4 h-4 mr-2" />Approuver
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Pending Validations Tab ───
function ValidationsTab({ isAdmin }) {
  const [items, setItems] = useState([]);
  const [hrItems, setHrItems] = useState([]);
  const [dirItems, setDirItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [target, setTarget] = useState(null);
  const [role, setRole] = useState('manager');

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const [mgrRes, hrRes, dirRes] = await Promise.all([
        api.get('/leave/manager/pending'),
        isAdmin ? api.get('/leave/requests?status=hr_pending') : Promise.resolve({ data: { items: [] } }),
        isAdmin ? api.get('/leave/director/pending') : Promise.resolve({ data: { items: [] } }),
      ]);
      setItems(mgrRes.data?.items || []);
      setHrItems(hrRes.data?.items || []);
      setDirItems(dirRes.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally { setLoading(false); }
  }, [isAdmin]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (loading) return <p className="text-sm text-slate-400 text-center py-8">Chargement...</p>;

  const Row = ({ r, decisionRole }) => (
    <Card data-testid={`validation-${r.id}`}>
      <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="w-3 h-3 rounded-full" style={{ backgroundColor: r.leaveType?.color || '#94a3b8' }} />
            <strong className="text-sm">{r.employee?.firstName} {r.employee?.lastName}</strong>
            <Badge variant="outline" className="text-xs">{r.leaveType?.name}</Badge>
            <Badge className={STATUS_BADGE[r.status]}>{STATUS_LABEL[r.status]}</Badge>
          </div>
          <p className="text-sm text-slate-600">{r.startDate} → {r.endDate} · <strong>{r.totalDays}</strong> j</p>
          {r.reason && <p className="text-xs text-slate-500 italic mt-0.5">"{r.reason}"</p>}
        </div>
        <Button size="sm" onClick={() => { setTarget(r); setRole(decisionRole); }} data-testid={`decide-${r.id}`}>
          Décider
        </Button>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-semibold mb-2 flex items-center gap-2">
          <Users className="w-4 h-4" />À valider en tant que Manager ({items.length})
        </h3>
        <div className="space-y-2">
          {items.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-4">Aucune demande en attente.</p>
          ) : items.map(r => <Row key={r.id} r={r} decisionRole="manager" />)}
        </div>
      </div>
      {isAdmin && (
        <div>
          <h3 className="font-semibold mb-2 flex items-center gap-2">
            <Settings className="w-4 h-4" />À valider en tant que RH ({hrItems.length})
          </h3>
          <div className="space-y-2">
            {hrItems.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">Aucune demande RH en attente.</p>
            ) : hrItems.map(r => <Row key={r.id} r={r} decisionRole="hr" />)}
          </div>
        </div>
      )}
      {isAdmin && (
        <div>
          <h3 className="font-semibold mb-2 flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-purple-600" />À valider en tant que Direction ({dirItems.length})
          </h3>
          <p className="text-xs text-slate-500 mb-2">Demandes des managers ayant des subordonnés — validation finale Direction.</p>
          <div className="space-y-2">
            {dirItems.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">Aucune escalade Direction en attente.</p>
            ) : dirItems.map(r => <Row key={r.id} r={r} decisionRole="director" />)}
          </div>
        </div>
      )}
      <DecisionDialog
        open={!!target} onOpenChange={(v) => { if (!v) setTarget(null); }}
        request={target} role={role} onDone={fetchAll}
      />
    </div>
  );
}

// ─── All Requests Tab ───
function AllRequestsTab() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    try {
      setLoading(true);
      const q = filter === 'all' ? '' : `?status=${filter}`;
      const res = await api.get(`/leave/requests${q}`);
      setItems(res.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { fetch(); }, [fetch]);

  return (
    <div className="space-y-3">
      <div className="flex gap-2 flex-wrap">
        {['all', 'manager_pending', 'hr_pending', 'approved', 'rejected', 'cancelled'].map(s => (
          <Button key={s} size="sm" variant={filter === s ? 'default' : 'outline'} onClick={() => setFilter(s)}>
            {s === 'all' ? 'Toutes' : STATUS_LABEL[s] || s}
          </Button>
        ))}
      </div>
      {loading ? <p className="text-sm text-slate-400 text-center py-4">Chargement...</p> :
        items.length === 0 ? <p className="text-sm text-slate-400 text-center py-4">Aucune demande.</p> :
          items.map(r => (
            <Card key={r.id}>
              <CardContent className="p-3 flex items-center justify-between gap-3 flex-wrap text-sm">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: r.leaveType?.color || '#94a3b8' }} />
                    <strong>{r.employee?.firstName} {r.employee?.lastName}</strong>
                    <Badge variant="outline" className="text-xs">{r.leaveType?.name}</Badge>
                    <Badge className={STATUS_BADGE[r.status]}>{STATUS_LABEL[r.status]}</Badge>
                  </div>
                  <p className="text-slate-600 text-xs">{r.startDate} → {r.endDate} · {r.totalDays} j</p>
                </div>
              </CardContent>
            </Card>
          ))
      }
    </div>
  );
}

// ─── Unplanned Absences Tab (Admin/Manager) ───
function UnplannedTab() {
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [types, setTypes] = useState([]);
  const [openForm, setOpenForm] = useState(false);
  const empty = { employeeId: '', type: 'late', date: new Date().toISOString().slice(0, 10),
                  hoursMissed: 0, days: 0, reason: '', impactPay: false, leaveTypeId: '' };
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    try {
      setLoading(true);
      const [u, e, t] = await Promise.all([
        api.get('/leave/unplanned'),
        api.get('/employees?limit=2000'),
        api.get('/leave/types'),
      ]);
      setItems(u.data?.items || []);
      const emps = (e.data?.employees || e.data || [])
        .filter(emp => (emp.status || 'Actif') === 'Actif');
      setEmployees(emps);
      setTypes(t.data?.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const submit = async () => {
    if (!form.employeeId || !form.date) {
      toast.error('Employé et date requis');
      return;
    }
    try {
      const payload = { ...form };
      if (!payload.leaveTypeId) delete payload.leaveTypeId;
      if (!payload.days) delete payload.days;
      await api.post('/leave/unplanned', payload);
      toast.success('Événement enregistré');
      setForm(empty);
      setOpenForm(false);
      fetch();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erreur'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer cet événement et sa demande liée ?')) return;
    try {
      await api.delete(`/leave/unplanned/${id}`);
      toast.success('Supprimé');
      fetch();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erreur'); }
  };

  const TYPE_LABEL = {
    late: 'Retard', early_leave: 'Départ anticipé',
    sick_post_factum: 'Maladie a posteriori',
    absent_no_notice: 'Absent sans préavis', abandonment: 'Abandon de poste',
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button onClick={() => setOpenForm(true)} data-testid="declare-unplanned-btn">
          <AlarmClock className="w-4 h-4 mr-2" />Déclarer un événement
        </Button>
      </div>
      {loading ? <p className="text-sm text-slate-400 text-center py-4">Chargement...</p> :
        items.length === 0 ? <p className="text-sm text-slate-400 text-center py-4">Aucun événement non planifié.</p> :
          items.map(u => (
            <Card key={u.id} data-testid={`unplanned-${u.id}`}>
              <CardContent className="p-3 flex items-center justify-between gap-3 flex-wrap text-sm">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge className="bg-amber-100 text-amber-700">{TYPE_LABEL[u.type] || u.type}</Badge>
                    <strong>{u.employee?.firstName} {u.employee?.lastName}</strong>
                    {u.impactPay && <Badge className="text-xs bg-red-100 text-red-700">Impact paie</Badge>}
                  </div>
                  <p className="text-xs text-slate-600">{u.date} {u.hoursMissed ? ` · ${u.hoursMissed}h` : ''}{u.days ? ` · ${u.days} j` : ''}</p>
                  {u.reason && <p className="text-xs italic text-slate-500 mt-0.5">"{u.reason}"</p>}
                </div>
                <Button size="sm" variant="ghost" onClick={() => remove(u.id)}>
                  <TrashIcon className="w-4 h-4 text-red-500" />
                </Button>
              </CardContent>
            </Card>
          ))
      }
      <Dialog open={openForm} onOpenChange={setOpenForm}>
        <DialogContent className="max-w-lg" data-testid="unplanned-dialog">
          <DialogHeader>
            <DialogTitle>Déclarer un événement non planifié</DialogTitle>
            <DialogDescription>Retard, départ anticipé, maladie a posteriori, absence sans préavis, abandon de poste.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Employé *</Label>
              <Select value={form.employeeId} onValueChange={(v) => setForm({ ...form, employeeId: v })}>
                <SelectTrigger data-testid="unplanned-emp"><SelectValue placeholder="Choisir" /></SelectTrigger>
                <SelectContent>
                  {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.firstName} {e.lastName}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type *</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(TYPE_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date *</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
              </div>
              <div>
                <Label>Heures manquées</Label>
                <Input type="number" step={0.5} min={0} value={form.hoursMissed}
                  onChange={(e) => setForm({ ...form, hoursMissed: parseFloat(e.target.value) || 0 })} />
              </div>
              <div>
                <Label>Jours</Label>
                <Input type="number" step={0.5} min={0} value={form.days}
                  onChange={(e) => setForm({ ...form, days: parseFloat(e.target.value) || 0 })} />
              </div>
            </div>
            <div>
              <Label>Imputer sur un type d'absence (optionnel)</Label>
              <Select value={form.leaveTypeId || 'none'} onValueChange={(v) => setForm({ ...form, leaveTypeId: v === 'none' ? '' : v })}>
                <SelectTrigger><SelectValue placeholder="Non" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Aucun —</SelectItem>
                  {types.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Commentaire</Label>
              <Textarea rows={2} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Switch checked={form.impactPay} onCheckedChange={(v) => setForm({ ...form, impactPay: v })} />
              Impact sur la paie (retenue)
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenForm(false)}>Annuler</Button>
            <Button onClick={submit} data-testid="unplanned-submit">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Attachments Validation Tab (Admin only) ───
function AttachmentsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    try {
      setLoading(true);
      // Récupère tous les justificatifs en attente via leave_requests + attachments par request
      // Simplification : on aggrège les requests ayant des attachments
      const reqs = await api.get('/leave/requests');
      const allItems = reqs.data?.items || [];
      const result = [];
      for (const r of allItems.slice(0, 50)) {
        try {
          const a = await api.get(`/leave/requests/${r.id}/attachments`);
          (a.data?.items || []).forEach(att => result.push({ ...att, request: r }));
        } catch {}
      }
      setItems(result.filter(a => a.status === 'pending'));
    } catch (err) { toast.error('Erreur'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const decide = async (id, decision) => {
    try {
      await api.put(`/leave/attachments/${id}/decision`, { decision });
      toast.success(decision === 'validate' ? 'Validé' : 'Rejeté');
      fetch();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  const download = async (att) => {
    try {
      const r = await api.get(`/leave/attachments/${att.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = att.fileName; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur téléchargement');
    }
  };

  if (loading) return <p className="text-sm text-slate-400 text-center py-4">Chargement des justificatifs...</p>;
  if (items.length === 0) return <p className="text-sm text-slate-400 text-center py-4">Aucun justificatif en attente de validation.</p>;
  return (
    <div className="space-y-2">
      {items.map(a => (
        <Card key={a.id}>
          <CardContent className="p-3 flex items-center justify-between gap-3 flex-wrap text-sm">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-0.5">
                <Badge variant="outline" className="text-xs">{a.documentType}</Badge>
                <strong className="text-sm truncate">{a.fileName}</strong>
              </div>
              <p className="text-xs text-slate-500">
                Demande : {a.request?.leaveType?.name} ({a.request?.startDate} → {a.request?.endDate})
              </p>
            </div>
            <div className="flex gap-1.5 flex-shrink-0">
              <Button size="sm" variant="outline" onClick={() => download(a)}>
                <DownloadIcon className="w-3.5 h-3.5" />
              </Button>
              <Button size="sm" variant="outline" onClick={() => decide(a.id, 'validate')}
                className="text-emerald-600">
                <Check className="w-3.5 h-3.5" />
              </Button>
              <Button size="sm" variant="outline" onClick={() => decide(a.id, 'reject')}
                className="text-red-600">
                <X className="w-3.5 h-3.5" />
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ─── Leave Types Config Tab ───
function TypesConfigTab() {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/leave/types');
      setTypes(res.data?.items || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const seedDefaults = async () => {
    try {
      const res = await api.post('/leave/types/seed-defaults');
      toast.success(`${res.data.added} type(s) ajouté(s)`);
      fetch();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  const runAccrual = async () => {
    try {
      const res = await api.post('/leave/balances/run-accrual');
      toast.success(`Acquisition lancée : ${res.data.processed} employé(s) traités`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2 flex-wrap">
        <Button onClick={seedDefaults} variant="outline" data-testid="seed-types-btn">
          <Plus className="w-4 h-4 mr-2" />Charger les 14 types par défaut
        </Button>
        <Button onClick={runAccrual} variant="outline" data-testid="run-accrual-btn">
          <RefreshCcw className="w-4 h-4 mr-2" />Lancer l'acquisition mensuelle
        </Button>
      </div>
      {loading ? <p className="text-sm text-slate-400 text-center py-4">Chargement...</p> :
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {types.map(t => (
            <Card key={t.id} data-testid={`type-card-${t.code}`}>
              <CardContent className="p-3 flex items-center gap-3">
                <span className="w-4 h-4 rounded" style={{ backgroundColor: t.color }} />
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-sm">{t.name}</p>
                  <div className="flex items-center gap-1 flex-wrap mt-0.5">
                    <Badge variant="outline" className="text-xs">{t.code}</Badge>
                    {t.paid ? <Badge className="text-xs bg-emerald-100 text-emerald-700">Payé</Badge> : <Badge className="text-xs bg-slate-100">Non payé</Badge>}
                    {t.impactsBalance && <Badge className="text-xs bg-blue-100 text-blue-700">Impacte solde</Badge>}
                    {t.requiresJustification && <Badge variant="outline" className="text-xs">Justificatif</Badge>}
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Min {t.minDays}j · Max {t.maxDays}j · Préavis {t.minNoticeDays}j
                    {t.defaultMonthlyAccrual > 0 && ` · Acquisition ${t.defaultMonthlyAccrual}j/mois`}
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      }
    </div>
  );
}

// ─── Rules Tab ───
function RulesTab() {
  const [rules, setRules] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/leave/rules').then(r => setRules(r.data)).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    try {
      await api.put('/leave/rules', rules);
      toast.success('Règles mises à jour');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  if (loading || !rules) return <p className="text-sm text-slate-400 text-center py-4">Chargement...</p>;

  return (
    <div className="space-y-4 max-w-2xl">
      <Card>
        <CardContent className="p-4 space-y-3">
          <h3 className="font-semibold">Calcul des jours</h3>
          <label className="flex items-center justify-between text-sm">
            <span>Jours ouvrés uniquement (exclure samedi/dimanche)</span>
            <Switch checked={rules.workingDaysOnly} onCheckedChange={(v) => setRules({ ...rules, workingDaysOnly: v })} />
          </label>
          <label className="flex items-center justify-between text-sm">
            <span>Samedi travaillé</span>
            <Switch checked={rules.saturdayWorked} onCheckedChange={(v) => setRules({ ...rules, saturdayWorked: v })} />
          </label>
          <label className="flex items-center justify-between text-sm">
            <span>Inclure les jours fériés dans le décompte</span>
            <Switch checked={rules.includeHolidays} onCheckedChange={(v) => setRules({ ...rules, includeHolidays: v })} />
          </label>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-4 space-y-3">
          <h3 className="font-semibold">Plafonds & seuils</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Préavis minimum (jours)</Label>
              <Input type="number" min={0} value={rules.globalMinNoticeDays || 0}
                onChange={(e) => setRules({ ...rules, globalMinNoticeDays: parseInt(e.target.value, 10) || 0 })} />
            </div>
            <div>
              <Label>Droit annuel (jours)</Label>
              <Input type="number" min={0} value={rules.annualEntitlement || 0}
                onChange={(e) => setRules({ ...rules, annualEntitlement: parseFloat(e.target.value) || 0 })} />
            </div>
            <div>
              <Label>Report maximum (jours)</Label>
              <Input type="number" min={0} value={rules.maxCarryOver || 0}
                onChange={(e) => setRules({ ...rules, maxCarryOver: parseFloat(e.target.value) || 0 })} />
            </div>
            <div>
              <Label>Seuil critique absences/dept</Label>
              <Input type="number" min={1} value={rules.criticalAbsenceThreshold || 3}
                onChange={(e) => setRules({ ...rules, criticalAbsenceThreshold: parseInt(e.target.value, 10) || 3 })} />
            </div>
          </div>
        </CardContent>
      </Card>
      <Button onClick={save} data-testid="save-rules-btn">Enregistrer les règles</Button>
    </div>
  );
}

// ─── Main Page ───
export default function AbsencesAdminPage() {
  const { user } = useAuth();
  const isAdmin = ['Admin', 'SuperAdmin'].includes(user?.role);

  return (
    <DashboardLayout>
      <div className="p-6 max-w-7xl mx-auto" data-testid="absences-admin-page">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Calendar className="w-6 h-6 text-indigo-600" />
            Gestion des absences
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {isAdmin
              ? 'Validations, suivi global, configuration des types et règles RH.'
              : 'Validez les demandes de votre équipe et consultez le planning.'}
          </p>
        </div>

        {isAdmin && <AbsencesOnboardingCard />}

        <Tabs defaultValue="validations">
          <TabsList className="bg-slate-100 flex-wrap h-auto">
            <TabsTrigger value="validations"><CheckCircle2 className="w-4 h-4 mr-2" />Validations</TabsTrigger>
            <TabsTrigger value="all"><Users className="w-4 h-4 mr-2" />Toutes les demandes</TabsTrigger>
            <TabsTrigger value="unplanned"><AlarmClock className="w-4 h-4 mr-2" />Non planifiées</TabsTrigger>
            {isAdmin && <TabsTrigger value="attachments"><Layers className="w-4 h-4 mr-2" />Justificatifs</TabsTrigger>}
            {isAdmin && <TabsTrigger value="types"><Layers className="w-4 h-4 mr-2" />Types & soldes</TabsTrigger>}
            {isAdmin && <TabsTrigger value="rules"><Settings className="w-4 h-4 mr-2" />Règles RH</TabsTrigger>}
          </TabsList>
          <TabsContent value="validations" className="mt-4"><ValidationsTab isAdmin={isAdmin} /></TabsContent>
          <TabsContent value="all" className="mt-4"><AllRequestsTab /></TabsContent>
          <TabsContent value="unplanned" className="mt-4"><UnplannedTab /></TabsContent>
          {isAdmin && <TabsContent value="attachments" className="mt-4"><AttachmentsTab /></TabsContent>}
          {isAdmin && <TabsContent value="types" className="mt-4"><TypesConfigTab /></TabsContent>}
          {isAdmin && <TabsContent value="rules" className="mt-4"><RulesTab /></TabsContent>}
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
